import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase";
import LoadingSpinner from "@/components/LoadingSpinner";

type LeadTier = "hot" | "warm" | "cold";
type LeadStatus = "new" | "contacted" | "sold";

interface Lead {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  propertyOwnership?: string;
  averageMonthlyBill?: string;
  createdAt?: string;
  leadScore?: number;
  leadTier?: LeadTier;
  status?: LeadStatus;
}

const TIER_STYLE: Record<LeadTier, React.CSSProperties> = {
  hot: { background: "#fee2e2", color: "#b91c1c" },
  warm: { background: "#fef3c7", color: "#92400e" },
  cold: { background: "#e5e7eb", color: "#374151" },
};

const STATUS_FLOW: LeadStatus[] = ["new", "contacted", "sold"];

export default function LeadsAdmin() {
  const [user, authLoading] = useAuthState(auth);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState<"all" | LeadTier>("all");
  const [stateFilter, setStateFilter] = useState("");

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "quotes"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLeads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Lead, "id">) })));
        setLoading(false);
      },
      (err) => {
        console.error("Error loading leads:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  const visible = useMemo(() => {
    return leads.filter((l) => {
      if (tierFilter !== "all" && l.leadTier !== tierFilter) return false;
      if (stateFilter && (l.state ?? "").toLowerCase() !== stateFilter.toLowerCase())
        return false;
      return true;
    });
  }, [leads, tierFilter, stateFilter]);

  const advanceStatus = async (lead: Lead) => {
    const current = lead.status ?? "new";
    const next = STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(current) + 1, STATUS_FLOW.length - 1)];
    if (next === current) return;
    try {
      await updateDoc(doc(db, "quotes", lead.id), { status: next });
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Leads</h1>
        <span className="text-sm text-gray-500">
          {visible.length} of {leads.length} shown
        </span>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value as "all" | LeadTier)}
          className="border rounded px-3 py-2 text-sm"
        >
          <option value="all">All tiers</option>
          <option value="hot">🔥 Hot</option>
          <option value="warm">🟡 Warm</option>
          <option value="cold">⚪️ Cold</option>
        </select>
        <input
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          placeholder="Filter by state (e.g. CA)"
          className="border rounded px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : visible.length === 0 ? (
        <p className="text-gray-500 py-16 text-center">No leads match these filters.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Score</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Bill</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Received</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => {
                const tier = (l.leadTier ?? "cold") as LeadTier;
                return (
                  <tr key={l.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase"
                        style={TIER_STYLE[tier]}
                      >
                        {tier}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono">{l.leadScore ?? "—"}</td>
                    <td className="px-3 py-2">
                      {`${l.firstName ?? ""} ${l.lastName ?? ""}`.trim() || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {l.phone ? <a className="text-blue-600" href={`tel:${l.phone}`}>{l.phone}</a> : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {[l.city, l.state].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-3 py-2">{l.averageMonthlyBill ?? "—"}</td>
                    <td className="px-3 py-2">{l.propertyOwnership ?? "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {l.createdAt ? new Date(l.createdAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => advanceStatus(l)}
                        disabled={(l.status ?? "new") === "sold"}
                        className="px-2 py-1 rounded border text-xs capitalize disabled:opacity-50"
                      >
                        {l.status ?? "new"}
                        {(l.status ?? "new") !== "sold" ? " →" : ""}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
